import { useState, useEffect } from "react";
import { loadSessions } from "./storage";
import type { SessionRecord } from "./types";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Barlow:wght@300;400;500;600&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --bg: #060b18; --bg2: #0a1020; --cyan: #00c8ff; --purple: #8b5cf6;
    --blue: #2563eb; --blue-h: #1d4ed8; --white: #f0f4ff;
    --muted: #4a5568; --border: rgba(255,255,255,0.07);
    --grad: linear-gradient(90deg, #00c8ff, #8b5cf6);
  }

  body { background: var(--bg); color: var(--white); font-family: 'Barlow', sans-serif; }

  .comp-wrap { min-height: 100vh; background: var(--bg); overflow-x: hidden; }

  .particles { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
  .particle {
    position: absolute; width: 2px; height: 2px;
    background: var(--cyan); border-radius: 50%; opacity: 0;
    animation: float linear infinite;
  }
  @keyframes float {
    0%   { opacity:0; transform:translateY(100vh) scale(0); }
    10%  { opacity:0.4; }
    90%  { opacity:0.2; }
    100% { opacity:0; transform:translateY(-10vh) scale(1.5); }
  }

  .topbar {
    position: sticky; top: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 28px;
    background: rgba(6,11,24,0.92); backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
  }
  .logo {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 900;
    font-size: 20px; letter-spacing: 2px;
    background: var(--grad); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .logo span { font-weight:300; font-size:11px; color:var(--muted); -webkit-text-fill-color:var(--muted); display:block; letter-spacing:3px; }
  .back-btn {
    background: transparent; border: 1px solid var(--border);
    color: var(--white); padding: 8px 18px; border-radius: 6px;
    font-family: 'Barlow', sans-serif; font-size: 13px; cursor: pointer; transition: all 0.2s;
  }
  .back-btn:hover { border-color: var(--cyan); color: var(--cyan); }

  .content { position: relative; z-index: 1; max-width: 1100px; margin: 0 auto; padding: 32px 24px 60px; }

  .page-header { text-align: center; margin-bottom: 36px; }
  .page-title {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 900; font-size: 42px;
    background: var(--grad); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    letter-spacing: 1px;
  }
  .page-sub { font-size: 13px; color: var(--muted); margin-top: 6px; }

  .section-title {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 800;
    font-size: 13px; letter-spacing: 3px; color: var(--muted);
    text-transform: uppercase; margin-bottom: 14px;
  }

  /* Session header labels */
  .session-labels {
    display: grid; grid-template-columns: 1fr 80px 1fr;
    gap: 0; margin-bottom: 20px; text-align: center;
  }
  .sess-label-card {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 10px; padding: 14px 20px;
  }
  .sess-label-card.current { border-color: rgba(0,200,255,0.3); }
  .sess-label-card.previous { border-color: rgba(139,92,246,0.3); }
  .sess-name {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 800;
    font-size: 18px; letter-spacing: 1px;
  }
  .sess-name.current { color: var(--cyan); }
  .sess-name.previous { color: var(--purple); }
  .sess-date { font-size: 11px; color: var(--muted); margin-top: 3px; }
  .vs-badge {
    display: flex; align-items: center; justify-content: center;
    font-family: 'Barlow Condensed', sans-serif; font-weight: 900;
    font-size: 22px; color: var(--muted); letter-spacing: 2px;
  }

  /* Score rings comparison */
  .rings-compare {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 12px; padding: 24px; margin-bottom: 20px;
  }
  .rings-row {
    display: grid; grid-template-columns: repeat(5, 1fr);
    gap: 16px;
  }
  @media(max-width:600px){ .rings-row{grid-template-columns:repeat(3,1fr);} }
  .ring-col { display: flex; flex-direction: column; align-items: center; gap: 8px; }
  .ring-pair { display: flex; gap: 6px; align-items: center; }
  .ring-cat-label {
    font-family: 'Barlow Condensed', sans-serif; font-size: 11px;
    letter-spacing: 1px; text-transform: uppercase; color: var(--muted); text-align: center;
  }
  .delta-badge {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 700;
    font-size: 13px; padding: 2px 8px; border-radius: 20px;
  }
  .delta-up   { color: #22c55e; background: rgba(34,197,94,0.12); }
  .delta-down { color: #ef4444; background: rgba(239,68,68,0.12); }
  .delta-same { color: var(--muted); background: rgba(255,255,255,0.05); }

  /* Bar comparison */
  .bars-grid {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 14px; margin-bottom: 20px;
  }
  @media(max-width:700px){ .bars-grid{grid-template-columns:1fr;} }

  .bar-card {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 12px; padding: 20px;
  }
  .bar-card-title {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 800;
    font-size: 15px; letter-spacing: 1px; text-transform: uppercase;
    margin-bottom: 16px; color: var(--white);
  }
  .bar-row { margin-bottom: 14px; }
  .bar-meta { display: flex; justify-content: space-between; margin-bottom: 5px; }
  .bar-label { font-size: 11px; color: var(--muted); }
  .bar-vals { display: flex; gap: 10px; font-family: 'Barlow Condensed', sans-serif; font-size: 12px; font-weight: 700; }
  .bar-val-curr { color: var(--cyan); }
  .bar-val-prev { color: var(--purple); }
  .bar-track { height: 6px; background: rgba(255,255,255,0.05); border-radius: 3px; position: relative; margin-bottom: 4px; }
  .bar-fill {
    height: 100%; border-radius: 3px; position: absolute; top: 0; left: 0;
    transition: width 0.8s ease;
  }
  .bar-fill.curr { background: var(--cyan); opacity: 0.9; }
  .bar-fill.prev { background: var(--purple); opacity: 0.5; top: 0; }
  .bar-track-prev { height: 6px; background: rgba(255,255,255,0.05); border-radius: 3px; position: relative; }

  /* Sparkline */
  .trend-section {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 12px; padding: 20px; margin-bottom: 20px;
  }
  .trend-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 20px; }
  @media(max-width:700px){ .trend-grid{grid-template-columns:repeat(2,1fr);} }
  .trend-item { display: flex; flex-direction: column; gap: 6px; }
  .trend-label { font-size: 11px; color: var(--muted); letter-spacing: 1px; text-transform: uppercase; }
  .trend-vals { display: flex; justify-content: space-between; align-items: baseline; }
  .trend-curr {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 800; font-size: 24px;
  }

  /* Insight cards */
  .insights-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; }
  @media(max-width:700px){ .insights-grid{grid-template-columns:1fr;} }
  .insight-card {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 12px; padding: 18px;
  }
  .insight-icon { font-size: 22px; margin-bottom: 8px; }
  .insight-title { font-weight: 600; font-size: 14px; margin-bottom: 5px; }
  .insight-desc { font-size: 12px; color: var(--muted); line-height: 1.6; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  .fade-up { animation: fadeUp 0.4s ease both; }
  .d1{animation-delay:0.05s} .d2{animation-delay:0.1s} .d3{animation-delay:0.15s} .d4{animation-delay:0.2s}
`;



function delta(c: number, p: number) {
  const d = c - p;
  if (d > 0) return { label: `▲ ${d}`, cls: "delta-up" };
  if (d < 0) return { label: `▼ ${Math.abs(d)}`, cls: "delta-down" };
  return { label: "–", cls: "delta-same" };
}

function MiniRing({ score, color, size = 52 }: { score: number; color: string; size?: number }) {
  const r = size / 2 - 5; const circ = 2 * Math.PI * r; const dash = (score / 100) * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="4"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2 + 5} textAnchor="middle" fontSize="11" fontWeight="800"
        fontFamily="Barlow Condensed" fill="#f0f4ff">{score}</text>
    </svg>
  );
}

function BarRow({ label, curr: c, prev: p, max, unit = "" }: { label: string; curr: number; prev: number; max: number; unit?: string }) {
  const d = delta(c, p);
  return (
    <div className="bar-row">
      <div className="bar-meta">
        <span className="bar-label">{label}</span>
        <div className="bar-vals">
          <span className="bar-val-curr">{c}{unit}</span>
          <span className="bar-val-prev">{p}{unit}</span>
          <span className={`delta-badge ${d.cls}`}>{d.label}</span>
        </div>
      </div>
      <div className="bar-track">
        <div className="bar-fill curr" style={{ width: `${(c / max) * 100}%` }} />
      </div>
      <div className="bar-track-prev">
        <div className="bar-fill prev" style={{ width: `${(p / max) * 100}%` }} />
      </div>
    </div>
  );
}

function Sparkline({ values, color }: { values: number[]; color: string }) {
  const max = Math.max(...values); const min = Math.min(...values);
  const range = max - min || 1;
  const W = 100, H = 30;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * W},${H - ((v - min) / range) * H}`).join(" ");
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height: 30 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      <circle cx={(1 / (values.length - 1)) * W * (values.length - 1)} cy={H - ((values[values.length - 1] - min) / range) * H} r="2.5" fill={color} />
    </svg>
  );
}

const SCORE_CATS = [
  { key: "overall",  label: "Overall",  color: "#00c8ff" },
  { key: "smash",    label: "Smash",    color: "#8b5cf6" },
  { key: "footwork", label: "Footwork", color: "#22c55e" },
  { key: "landing",  label: "Landing",  color: "#fbbf24" },
  { key: "prep",     label: "Prep",     color: "#f472b6" },
];

const INSIGHTS = [
  { icon: "📈", title: "Overall up +7 pts", desc: "Consistent improvement across all categories. Strongest gain in Prep score (+11), showing better shot anticipation." },
  { icon: "🦵", title: "Jump height +5 cm", desc: "48 cm vs 43 cm last session. Improved explosive power — keep working on calf and quad strength." },
  { icon: "⚡", title: "Max speed +9 km/h", desc: "112 km/h smash speed vs 103 km/h. Wrist speed improved from 12.1 to 14.3 m/s — contributing directly to power." },
];

interface Props { onBack: () => void; }

export default function ComparisonPage({ onBack }: Props) {
  const [tab, setTab] = useState<"scores" | "metrics">("scores");
  const [sessions, setSessions] = useState<SessionRecord[]>([]);

  useEffect(() => { setSessions(loadSessions()); }, []);

  const curr = sessions[0] ?? null;
  const prev = sessions[1] ?? null;

  if (sessions.length < 2) {
    return (
      <>
        <style>{css}</style>
        <div className="comp-wrap" style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"100vh" }}>
          <div style={{ textAlign:"center", maxWidth:400 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>📊</div>
            <div style={{ fontFamily:"Barlow Condensed", fontSize:28, fontWeight:900, background:"linear-gradient(90deg,#00c8ff,#8b5cf6)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", marginBottom:8 }}>
              {sessions.length === 0 ? "No Sessions Yet" : "Need One More Session"}
            </div>
            <div style={{ color:"#4a5568", fontSize:14, marginBottom:28, lineHeight:1.6 }}>
              {sessions.length === 0
                ? "Record your first session to start tracking progress."
                : "Record one more session to compare your performance."}
            </div>
            <button style={{ background:"#2563eb", border:"none", borderRadius:8, color:"#fff", padding:"12px 28px", fontFamily:"Barlow", fontSize:14, fontWeight:600, cursor:"pointer" }} onClick={onBack}>← Back to Dashboard</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="comp-wrap">
        <div className="particles">
          {Array.from({ length: 14 }).map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              animationDuration: `${8 + Math.random() * 12}s`,
              animationDelay: `${Math.random() * 8}s`,
            }} />
          ))}
        </div>

        <div className="topbar">
          <div className="logo">SHUTTLECOCK <span>BY STATVO</span></div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="back-btn"
              style={tab === "scores" ? { borderColor: "var(--cyan)", color: "var(--cyan)" } : {}}
              onClick={() => setTab("scores")}
            >Scores</button>
            <button
              className="back-btn"
              style={tab === "metrics" ? { borderColor: "var(--cyan)", color: "var(--cyan)" } : {}}
              onClick={() => setTab("metrics")}
            >Metrics</button>
          </div>
          <button className="back-btn" onClick={onBack}>← Dashboard</button>
        </div>

        <div className="content">

          <div className="page-header fade-up">
            <div className="page-title">Session Comparison</div>
            <div className="page-sub">Track your progress between sessions</div>
          </div>

          {/* Session labels */}
          <div className="session-labels fade-up d1">
            <div className="sess-label-card current">
              <div className="sess-name current">{curr.label}</div>
              <div className="sess-date">{curr.date} · {curr.duration} · {curr.shots} shots</div>
            </div>
            <div className="vs-badge">VS</div>
            <div className="sess-label-card previous">
              <div className="sess-name previous">{prev.label}</div>
              <div className="sess-date">{prev.date} · {prev.duration} · {prev.shots} shots</div>
            </div>
          </div>

          {tab === "scores" && (
            <>
              {/* Score rings */}
              <div className="rings-compare fade-up d2">
                <div className="section-title">Form Scores</div>
                <div className="rings-row">
                  {SCORE_CATS.map(cat => {
                    const c = (curr as any)[cat.key];
                    const p = (prev as any)[cat.key];
                    const d = delta(c, p);
                    return (
                      <div className="ring-col" key={cat.key}>
                        <div className="ring-pair">
                          <MiniRing score={c} color={cat.color} size={56} />
                          <MiniRing score={p} color={cat.color} size={44} />
                        </div>
                        <div className="ring-cat-label">{cat.label}</div>
                        <span className={`delta-badge ${d.cls}`}>{d.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Trend sparklines */}
              <div className="trend-section fade-up d3">
                <div className="section-title">Trend (2 sessions)</div>
                <div className="trend-grid">
                  {[
                    { label: "Overall", vals: [prev.overall, curr.overall], color: "#00c8ff", unit: "" },
                    { label: "Jump Height", vals: [prev.jumpHeight, curr.jumpHeight], color: "#22c55e", unit: "cm" },
                    { label: "Max Speed", vals: [prev.maxSpeed, curr.maxSpeed], color: "#8b5cf6", unit: " km/h" },
                    { label: "Posture", vals: [prev.postureScore, curr.postureScore], color: "#fbbf24", unit: "" },
                  ].map(t => {
                    const d = delta(t.vals[1], t.vals[0]);
                    return (
                      <div className="trend-item" key={t.label}>
                        <div className="trend-label">{t.label}</div>
                        <div className="trend-vals">
                          <span className="trend-curr" style={{ color: t.color }}>{t.vals[1]}{t.unit}</span>
                          <span className={`delta-badge ${d.cls}`}>{d.label}</span>
                        </div>
                        <Sparkline values={t.vals} color={t.color} />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Insights */}
              <div className="section-title fade-up d4">Key Insights</div>
              <div className="insights-grid fade-up d4">
                {INSIGHTS.map((ins, i) => (
                  <div className="insight-card" key={i}>
                    <div className="insight-icon">{ins.icon}</div>
                    <div className="insight-title">{ins.title}</div>
                    <div className="insight-desc">{ins.desc}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "metrics" && (
            <div className="bars-grid fade-up d2">
              <div className="bar-card">
                <div className="bar-card-title">
                  <span style={{ color: "var(--cyan)" }}>■</span> Current &nbsp;
                  <span style={{ color: "var(--purple)", opacity: 0.6 }}>■</span> Previous
                </div>
                <BarRow label="Jump Height" curr={curr.jumpHeight} prev={prev.jumpHeight} max={80} unit=" cm" />
                <BarRow label="Max Speed" curr={curr.maxSpeed} prev={prev.maxSpeed} max={150} unit=" km/h" />
                <BarRow label="Avg Speed" curr={curr.avgSpeed} prev={prev.avgSpeed} max={120} unit=" km/h" />
                <BarRow label="Wrist Speed" curr={curr.wristSpeed} prev={prev.wristSpeed} max={20} unit=" m/s" />
              </div>
              <div className="bar-card">
                <div className="bar-card-title">
                  <span style={{ color: "var(--cyan)" }}>■</span> Current &nbsp;
                  <span style={{ color: "var(--purple)", opacity: 0.6 }}>■</span> Previous
                </div>
                <BarRow label="Displacement" curr={curr.displacement} prev={prev.displacement} max={500} unit=" m" />
                <BarRow label="Shots" curr={curr.shots} prev={prev.shots} max={60} />
                <BarRow label="Posture Score" curr={curr.postureScore} prev={prev.postureScore} max={100} />
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}